import { useState } from "react";
import { X, Loader2, Lock, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { apiViewer, formatApiError } from "@/lib/api";
import OtpResetForm from "@/components/OtpResetForm";

const INPUT_CLS =
  "w-full rounded-xl border border-stone-700/70 bg-ink-900/80 px-4 py-3 text-sm text-stone-100 placeholder:text-stone-500 transition-colors focus:border-gold-500/60 focus:outline-none focus:ring-1 focus:ring-gold-500/40";
const LABEL_CLS = "mb-2 block text-[10px] uppercase tracking-[0.3em] text-stone-400";

export default function AuthModal({ initialMode = "signin", onClose, onAuthed }) {
  const [mode, setMode] = useState(initialMode);
  const [name, setName] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const switchMode = (m) => {
    setMode(m);
    setPassword("");
  };

  const submitAuth = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const url = mode === "register" ? "/auth/register" : "/auth/login";
      const payload = mode === "register" ? { name, identifier, password } : { identifier, password };
      const { data } = await apiViewer.post(url, payload);
      localStorage.setItem("viewna_viewer_token", data.access_token);
      toast.success(
        mode === "register" ? `Welcome to VIEWNA, ${data.user.name}` : `Welcome back, ${data.user.name}`
      );
      onAuthed(data.user, data.access_token);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const tabCls = (active) =>
    `flex-1 rounded-full border py-2 text-[11px] font-bold uppercase tracking-[0.2em] transition-all duration-300 ${
      active
        ? "border-gold-500/40 bg-gold-500/15 text-gold-300"
        : "border-transparent text-stone-400 hover:text-gold-300"
    }`;

  const modalTitle = mode === "register" ? "Create Account" : mode === "forgot" ? "Forgot Password" : "Viewer Sign In";
  const modalIcon = mode === "register" ? UserIcon : mode === "forgot" ? Lock : Lock;
  const modalSub =
    mode === "register"
      ? "One account for your watchlist, likes and more."
      : mode === "forgot"
        ? "Reset your password with a one-time code."
        : "Save shows to your personal watchlist, always yours.";

  return (
    <div data-testid="auth-modal" className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/90 backdrop-blur-md" data-testid="auth-modal-backdrop" onClick={onClose} />
      <div className="relative max-h-[92vh] w-full max-w-md overflow-y-auto rounded-2xl border border-gold-500/30 bg-[#0c0a07] p-8 shadow-[0_0_100px_rgba(212,175,55,0.15)] sm:p-10">
        <button
          data-testid="auth-modal-close-button"
          aria-label="Close"
          onClick={onClose}
          className="absolute right-5 top-5 rounded-full border border-stone-700 p-2 text-stone-300 transition-colors hover:border-gold-500/60 hover:text-gold-400"
        >
          <X className="h-4 w-4" />
        </button>

        <span className="flex h-14 w-14 items-center justify-center rounded-full border border-gold-500/40 bg-gold-500/10 text-gold-400">
          {(() => { const Icon = modalIcon; return <Icon className="h-6 w-6" />; })()}
        </span>
        <h2 className="mt-5 font-display text-2xl font-bold uppercase tracking-[0.2em] text-gold-300">{modalTitle}</h2>
        <p className="mt-2 text-sm text-stone-400">{modalSub}</p>

        {mode === "forgot" ? (
          <div className="mt-7">
            <OtpResetForm
              initialIdentifier={identifier}
              onBack={() => switchMode("signin")}
              onDone={(msg) => {
                toast.success(msg || "Password updated successfully! Please Sign In.");
                switchMode("signin");
              }}
            />
          </div>
        ) : (
          <>
            <div className="mt-6 flex gap-2 rounded-full border border-stone-800 p-1">
              <button data-testid="auth-tab-signin" onClick={() => switchMode("signin")} className={tabCls(mode === "signin")}>
                Sign In
              </button>
              <button data-testid="auth-tab-register" onClick={() => switchMode("register")} className={tabCls(mode === "register")}>
                Create Account
              </button>
            </div>

            <form onSubmit={submitAuth} data-testid="auth-form" className="mt-6 space-y-4">
              {mode === "register" && (
                <div>
                  <label htmlFor="auth-name" className={LABEL_CLS}>Full Name</label>
                  <input
                    id="auth-name"
                    data-testid="auth-name-input"
                    required
                    placeholder="Your full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={INPUT_CLS}
                  />
                </div>
              )}
              <div>
                <label htmlFor="auth-identifier" className={LABEL_CLS}>
                  {mode === "register" ? "Email Address or Mobile Number" : "Registered Email / Mobile Number"}
                </label>
                <input
                  id="auth-identifier"
                  data-testid="auth-identifier-input"
                  required
                  placeholder="you@example.com or 9876543210"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  className={INPUT_CLS}
                />
              </div>
              <div>
                <label htmlFor="auth-password" className={LABEL_CLS}>
                  {mode === "register" ? "Create Password" : "Password"}
                </label>
                <input
                  id="auth-password"
                  data-testid="auth-password-input"
                  type="password"
                  required
                  minLength={6}
                  placeholder={mode === "register" ? "Minimum 6 characters" : "Your password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={INPUT_CLS}
                />
                {mode === "signin" && (
                  <button
                    type="button"
                    data-testid="auth-forgot-link"
                    onClick={() => switchMode("forgot")}
                    className="mt-2 text-[11px] font-medium text-gold-400 underline underline-offset-4 transition-colors hover:text-gold-300"
                  >
                    Forgot Password?
                  </button>
                )}
              </div>
              <button
                type="submit"
                data-testid="auth-submit-button"
                disabled={busy}
                className="inline-flex w-full items-center justify-center gap-3 rounded-full bg-gradient-to-r from-gold-600 via-gold-400 to-gold-600 px-8 py-3.5 text-[12px] font-bold uppercase tracking-[0.2em] text-ink-950 transition-all duration-300 hover:shadow-[0_0_28px_rgba(212,175,55,0.4)] disabled:opacity-60"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                {mode === "register" ? "Create Account" : "Sign In"}
              </button>
            </form>

            <p className="mt-5 text-center text-xs text-stone-500">
              {mode === "register" ? (
                <>
                  Already have an account?{" "}
                  <button type="button" data-testid="auth-switch-to-signin" onClick={() => switchMode("signin")} className="text-gold-400 underline underline-offset-4">
                    Sign In
                  </button>
                </>
              ) : (
                <>
                  New User?{" "}
                  <button type="button" data-testid="auth-switch-to-register" onClick={() => switchMode("register")} className="text-gold-400 underline underline-offset-4">
                    Create Account / Sign Up
                  </button>
                </>
              )}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
